import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { z } from "zod";
import { CitationComparisonCard } from "~/components/duplicates/citation-comparison-card";
import { ResolutionActions } from "~/components/duplicates/resolution-actions";
import { useProjectContext } from "~/components/layout/project-context";
import { Button } from "~/components/ui/button";
import { $resolveDuplicateGroup } from "~/lib/duplicates/functions";
import {
  duplicateGroupDetailQueryOptions,
  duplicateGroupsQueryOptions,
} from "~/lib/duplicates/queries";
import { projectCitationsQueryOptions } from "~/lib/projects/queries";

const searchSchema = z.object({
  groupId: z.string().optional(),
  selectedId: z.string().optional(),
  mode: z.enum(["pending", "resolved"]).default("pending"),
});

export const Route = createFileRoute("/_auth/projects/$projectId/duplicates/review")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ mode: search.mode }),
  loader: async ({ context, params, deps }) => {
    const groups = await context.queryClient.ensureQueryData(
      duplicateGroupsQueryOptions(params.projectId),
    );

    const targetStatus = deps.mode === "resolved" ? "resolved" : "pending";
    const targetGroups = groups.filter((g) => g.status === targetStatus).slice(0, 3);
    await Promise.all(
      targetGroups.map((g) =>
        context.queryClient.ensureQueryData(
          duplicateGroupDetailQueryOptions(params.projectId, g.groupId),
        ),
      ),
    );
  },
  component: DuplicateReviewPage,
});

function DuplicateReviewPage() {
  const { projectId, projectTitle } = useProjectContext();
  const { groupId: searchGroupId, selectedId, mode: reviewMode } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: groups } = useSuspenseQuery(duplicateGroupsQueryOptions(projectId));

  const targetStatus = reviewMode === "resolved" ? "resolved" : "pending";
  const filteredGroups = useMemo(
    () => groups.filter((g) => g.status === targetStatus),
    [groups, targetStatus],
  );
  const currentGroupId = searchGroupId ?? filteredGroups[0]?.groupId;
  const currentGroupIndex = filteredGroups.findIndex((g) => g.groupId === currentGroupId);

  const lastGroupIdRef = useRef(currentGroupId);

  const selectedMasterId = useMemo(() => {
    if (lastGroupIdRef.current !== currentGroupId) {
      lastGroupIdRef.current = currentGroupId;
      return null;
    }
    return selectedId ?? null;
  }, [currentGroupId, selectedId]);

  const setSelectedMasterId = useCallback(
    (id: string | null) => {
      navigate({
        to: "/projects/$projectId/duplicates/review",
        params: { projectId },
        search: { groupId: currentGroupId, selectedId: id ?? undefined, mode: reviewMode },
        replace: true,
      });
    },
    [navigate, projectId, currentGroupId, reviewMode],
  );

  const { data: citations = [], error: citationsError } = useQuery({
    ...duplicateGroupDetailQueryOptions(projectId, currentGroupId ?? ""),
    enabled: !!currentGroupId,
  });

  useEffect(() => {
    if (citationsError) {
      console.error("[DuplicateReview] Citations fetch error:", citationsError);
    }
  }, [citationsError]);

  const goToNextGroup = useCallback(() => {
    const nextGroup = filteredGroups.find(
      (g) => g.groupId !== currentGroupId,
    );
    if (nextGroup) {
      navigate({
        to: "/projects/$projectId/duplicates/review",
        params: { projectId },
        search: { groupId: nextGroup.groupId, mode: reviewMode },
      });
    }
  }, [filteredGroups, currentGroupId, projectId, navigate, reviewMode]);

  const resolveMutation = useMutation({
    mutationFn: (params: {
      action: "confirm_duplicates" | "mark_unique";
      masterId?: string;
    }) =>
      $resolveDuplicateGroup({
        data: {
          projectId,
          groupId: currentGroupId!,
          action: params.action,
          masterId: params.masterId,
        },
      }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: ["projects", projectId, "duplicates", "groups"],
      });
      await queryClient.cancelQueries({ queryKey: ["projects", projectId, "citations"] });

      const previousGroups = queryClient.getQueryData<typeof groups>([
        "projects",
        projectId,
        "duplicates",
        "groups",
      ]);
      const previousCitations = queryClient.getQueryData(
        projectCitationsQueryOptions(projectId).queryKey,
      );

      if (previousGroups) {
        if (variables.action === "confirm_duplicates") {
          queryClient.setQueryData(
            ["projects", projectId, "duplicates", "groups"],
            previousGroups.map((g) =>
              g.groupId === currentGroupId ? { ...g, status: "resolved" as const } : g,
            ),
          );
        } else {
          queryClient.setQueryData(
            ["projects", projectId, "duplicates", "groups"],
            previousGroups.filter((g) => g.groupId !== currentGroupId),
          );
        }
      }

      if (previousCitations && Array.isArray(previousCitations)) {
        const citationIdsInGroup = citations.map((c) => c.id);

        const updatedCitations = previousCitations.map(
          (citation: (typeof previousCitations)[number]) => {
            if (!citationIdsInGroup.includes(citation.id)) {
              return citation;
            }

            if (variables.action === "confirm_duplicates") {
              if (citation.id === variables.masterId) {
                return { ...citation, duplicateStatus: "master" as const };
              }
              return {
                ...citation,
                duplicateStatus: "confirmed_duplicate" as const,
                screeningStatus: "excluded" as const,
                exclusionReason: "Duplicate",
                exclusionCode: "duplicate",
              };
            } else {
              return {
                ...citation,
                duplicateStatus: "unique" as const,
                duplicateGroupId: null,
              };
            }
          },
        );

        queryClient.setQueryData(
          projectCitationsQueryOptions(projectId).queryKey,
          updatedCitations,
        );
      }

      const nextGroup = previousGroups
        ?.filter((g) => g.status === targetStatus)
        .find((g) => g.groupId !== currentGroupId);
      if (nextGroup) {
        queryClient.prefetchQuery(
          duplicateGroupDetailQueryOptions(projectId, nextGroup.groupId),
        );
      }

      return { previousGroups, previousCitations };
    },
    onError: (err, variables, context) => {
      console.error("[DuplicateReview] Mutation error:", err, {
        variables,
        currentGroupId,
      });
      if (context?.previousGroups) {
        queryClient.setQueryData(
          ["projects", projectId, "duplicates", "groups"],
          context.previousGroups,
        );
      }
      if (context?.previousCitations) {
        queryClient.setQueryData(
          projectCitationsQueryOptions(projectId).queryKey,
          context.previousCitations,
        );
      }
    },
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: ["projects", projectId, "duplicates", "groups", currentGroupId],
      });
      goToNextGroup();
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "duplicates", "groups"],
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "citations"],
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "pipeline"],
        refetchType: "none",
      });
    },
  });

  const { mutate, isPending, error: mutationError } = resolveMutation;

  useEffect(() => {
    if (mutationError) {
      console.error("[DuplicateReview] Mutation failed:", mutationError);
    }
  }, [mutationError]);

  const handleConfirmDuplicates = useCallback(() => {
    if (selectedMasterId) {
      mutate({ action: "confirm_duplicates", masterId: selectedMasterId });
    }
  }, [selectedMasterId, mutate]);

  const handleMarkUnique = useCallback(() => {
    mutate({ action: "mark_unique" });
  }, [mutate]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if (e.key >= "1" && e.key <= "9") {
        const index = parseInt(e.key, 10) - 1;
        if (citations && index < citations.length) {
          setSelectedMasterId(citations[index].id);
        }
        return;
      }

      if (e.key === "Enter" && selectedMasterId && !isPending) {
        e.preventDefault();
        handleConfirmDuplicates();
        return;
      }

      if ((e.key === "u" || e.key === "U") && !isPending) {
        e.preventDefault();
        handleMarkUnique();
        return;
      }

      if ((e.key === "s" || e.key === "S") && !isPending) {
        e.preventDefault();
        goToNextGroup();
        return;
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    citations,
    selectedMasterId,
    isPending,
    handleConfirmDuplicates,
    handleMarkUnique,
    goToNextGroup,
    setSelectedMasterId,
  ]);

  if (filteredGroups.length === 0) {
    return (
      <div className="flex min-h-screen flex-col">
        <main className="flex-1 p-6">
          <div className="rounded-lg border p-8 text-center">
            <h2 className="mb-2 text-xl font-semibold">
              {reviewMode === "resolved" ? "No Resolved Groups" : "All Done!"}
            </h2>
            <p className="text-muted-foreground mb-4">
              {reviewMode === "resolved"
                ? "No resolved groups to review."
                : "All duplicate groups have been reviewed."}
            </p>
            <Button render={<Link to="/projects/$projectId/duplicates" params={{ projectId }} />}>
              Back to Duplicates
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (!currentGroupId) {
    return (
      <div className="flex min-h-screen flex-col">
        <main className="flex-1 p-6">
          <div className="text-muted-foreground">No group selected.</div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <div className="bg-muted/30 border-b px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="text-muted-foreground text-sm">
            Select the master citation to keep
          </div>
          <div className="text-muted-foreground text-sm">
            Group {currentGroupIndex + 1} of {filteredGroups.length}
          </div>
        </div>
      </div>

      <main className="p-6">
        {(mutationError || citationsError) && (
          <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
            <strong>Error:</strong>{" "}
            {mutationError?.message || citationsError?.message || "Something went wrong"}
          </div>
        )}

        <div className="bg-muted/30 mb-6 rounded-lg border p-4">
          <p className="text-sm">
            Select the <strong>master citation</strong> to keep. Other citations in this
            group will be marked as duplicates and excluded from screening.
          </p>
        </div>

        <div className="mb-6 grid gap-4 md:grid-cols-2">
          {citations.map((citation, index) => (
            <CitationComparisonCard
              key={citation.id}
              citation={citation}
              isSelected={selectedMasterId === citation.id}
              onSelect={() => setSelectedMasterId(citation.id)}
              index={index}
            />
          ))}
        </div>

        <div className="bg-background sticky bottom-0 rounded-lg border p-4">
          <ResolutionActions
            onConfirmDuplicates={handleConfirmDuplicates}
            onMarkUnique={handleMarkUnique}
            onSkip={goToNextGroup}
            hasSelection={!!selectedMasterId}
            isLoading={isPending}
            canSkip={filteredGroups.length > 1}
          />
        </div>
      </main>
    </div>
  );
}
